import React, { useEffect, useState } from 'react';

import { MailOutlined, UserOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  ConfigProvider,
  Flex,
  Form,
  GetProp,
  Input,
  Modal,
  Popconfirm,
  Spin,
  Typography,
  message,
} from 'antd';
import { OTPProps } from 'antd/es/input/OTP';
import Password from 'antd/es/input/Password';
import Paragraph from 'antd/es/typography/Paragraph';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ApiErrorData } from 'models/ApiResponse';
import { User } from 'models/User';

import { AuthService } from 'services/AuthService';
import { UserService } from 'services/UserService';

import { getFormattedDashboardConfig } from 'utils/AuthUtils';
import { FunctionsHelper } from 'utils/FunctionsHelper';

import { LOGO_OHLA, OHLA_DEFAULT_PRIMARY_COLOR } from '../../config/Constants';
import appI18n from '../../config/i18n';
import AuthAction from '../../redux/actions/authActions/Actions';
import DashboardConfigAction from '../../redux/actions/dashboardConfigActions/Action';
import ImpersonationAction from '../../redux/actions/impersonationActions/Actions';
import IntlAction from '../../redux/actions/intlActions/Actions';

function OhlaLogin() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { t, i18n } = useTranslation(undefined, { i18n: appI18n });

  /* Password recovery modal settings */
  const [passwordRecoveryLoading, setPasswordRecoveryLoading] = useState(false);
  const [isPasswordRecoveryModalOpen, setIsPasswordRecoveryModalOpen] =
    useState(false);
  const [emailInputRecoveryPassword, setEmailInputRecoveryPassword] =
    useState('');
  const [emailInputHaveError, setEmailInputHaveError] = useState(false);

  /* Verify user modal settings */
  const [isVerifyUserModalOpen, setIsVerifyUserModalOpen] = useState(false);
  const [noVerifyUserData, setNoVerifyUserData] = useState<{
    id: number;
    username: string;
    password: string;
  } | null>(null);

  /* OTP */
  const [otpLoading, setOtpLoading] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [expiredOtp, setExpiredOtp] = useState<Date>(new Date());
  const [progressOtp, setProgressOtp] = useState(0);
  const [otpCode, setOtpCode] = useState('');
  const [otpInputHaveError, setOtpInputHaveError] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs();
      const total = 280;
      const remaining = now.diff(dayjs(expiredOtp), 'second');

      if (remaining > 0) {
        const perc = 100 - Math.floor((100 * (total - remaining)) / total);
        setProgressOtp(perc);
      } else {
        setProgressOtp(0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const onFinish = async (values: any) => {
    if (!loading) {
      const loadingMessageKey = 'loading-message';
      messageApi.open({
        key: loadingMessageKey,
        type: 'loading',
        content: t(
          'scenes.login.ohla_login.scene.messages.checking_credentials'
        ),
        duration: 0,
      });
      setLoading(true);

      const loginResponse = await AuthService.login({
        username: values.username,
        password: values.password,
      });

      messageApi.destroy(loadingMessageKey);

      if (loginResponse.success) {
        messageApi.success(
          t('scenes.login.ohla_login.scene.messages.login_success'),
          3.5
        );
        const currentUser = loginResponse.data as User;
        dispatch(ImpersonationAction.clearImpersonation());
        dispatch(AuthAction.signIn(currentUser));
        const dashboardConfig = getFormattedDashboardConfig(currentUser);
        dispatch(DashboardConfigAction.save(dashboardConfig));
        const langFromCountry = (
          currentUser.company?.country?.language || ''
        ).toLowerCase();
        if (langFromCountry === 'es' || langFromCountry === 'en') {
          localStorage.setItem('userLang', langFromCountry);
          appI18n.changeLanguage(langFromCountry);
          dispatch(IntlAction.update({ lang: langFromCountry }));
        }
      } else {
        const error = loginResponse.data as ApiErrorData;
        if (error.code === 'user.unverifiedUser') {
          const payload: {
            userId: number;
            activationCodeExpiredAt: string | null;
          } = JSON.parse(error.payload as string);

          if (payload.activationCodeExpiredAt) {
            const expirationDate = dayjs(payload.activationCodeExpiredAt)
              .subtract(20, 'second')
              .toDate();

            if (expirationDate >= new Date()) {
              setExpiredOtp(expirationDate);
              setNoVerifyUserData({
                id: payload.userId,
                username: values.username,
                password: values.password,
              });
              showOtpModal();
            } else {
              showVerifyUserModal(
                payload.userId,
                values.username,
                values.password
              );
            }
          } else {
            showVerifyUserModal(
              payload.userId,
              values.username,
              values.password
            );
          }
        } else {
          messageApi.error(
            (error.message as string) ||
              t('scenes.login.ohla_login.scene.errors.login_failed'),
            3.5
          );
        }
      }

      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
    messageApi.error(
      t('scenes.login.ohla_login.scene.errors.form_invalid'),
      3.5
    );
  };

  const showPasswordRecoveryModal = () => {
    setEmailInputRecoveryPassword('');
    setEmailInputHaveError(false);
    setIsPasswordRecoveryModalOpen(true);
  };

  const handlePasswordRecoveryOk = async () => {
    if (!emailInputRecoveryPassword) {
      setEmailInputHaveError(true);
      messageApi.error(
        t(
          'scenes.login.ohla_login.components.password_recovery_modal.validations.user_account.required'
        ),
        3.5
      );
      return;
    }

    if (!FunctionsHelper.validateEmail(emailInputRecoveryPassword)) {
      setEmailInputHaveError(true);
      messageApi.error(
        t(
          'scenes.login.ohla_login.components.password_recovery_modal.validations.user_account.invalid'
        ),
        3.5
      );
      return;
    }

    setPasswordRecoveryLoading(true);

    const sendPasswordRecoveryResponse = await UserService.sendPasswordRecovery(
      emailInputRecoveryPassword
    );

    if (sendPasswordRecoveryResponse.success) {
      messageApi.success(
        t(
          'scenes.login.ohla_login.components.password_recovery_modal.messages.send_link_success'
        ),
        3.5
      );
      setIsPasswordRecoveryModalOpen(false);
    } else {
      const error = sendPasswordRecoveryResponse.data as ApiErrorData;
      messageApi.error(
        (error.message as string) ||
          t(
            'scenes.login.ohla_login.components.password_recovery_modal.errors.send_link_failed'
          ),
        3.5
      );
    }

    setPasswordRecoveryLoading(false);
  };

  const handlePasswordRecoveryCancel = () => {
    setIsPasswordRecoveryModalOpen(false);
  };

  const showVerifyUserModal = (
    selectedUserId: number,
    selectedUserName: string,
    selectedUserPassword: string
  ) => {
    setNoVerifyUserData({
      id: selectedUserId,
      username: selectedUserName,
      password: selectedUserPassword,
    });
    setIsVerifyUserModalOpen(true);
  };

  const handleVerifyUserOk = async () => {
    if (noVerifyUserData?.id) {
      setIsVerifyUserModalOpen(false);
      setLoading(true);

      messageApi.open({
        type: 'loading',
        content: t(
          'scenes.login.ohla_login.scene.messages.sending_activation_code'
        ),
        duration: 0,
      });

      const sendActivationCodeResponse =
        await UserService.sendNewActivationCode(noVerifyUserData.id);

      messageApi.destroy();

      if (sendActivationCodeResponse.success) {
        const sendActivationCodeResponseData =
          sendActivationCodeResponse.data as {
            executedAction: boolean;
            userId: number;
            expiredCodeAt: Date;
          };
        messageApi.success(
          t(
            'scenes.login.ohla_login.scene.messages.send_activation_code_success'
          ),
          3.5
        );
        setExpiredOtp(
          dayjs(sendActivationCodeResponseData.expiredCodeAt)
            .subtract(20, 'second')
            .toDate()
        );
        showOtpModal();
      } else {
        const error = sendActivationCodeResponse.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t(
              'scenes.login.ohla_login.scene.errors.send_activation_code_failed'
            ),
          3.5
        );
      }

      setLoading(false);
    }
  };

  const handleVerifyUserCancel = () => {
    setIsVerifyUserModalOpen(false);
  };

  const showOtpModal = () => {
    setIsOtpModalOpen(true);
  };

  const handleOtpOk = async () => {
    if (otpCode && noVerifyUserData) {
      setOtpLoading(true);

      const verifyAccountResponse = await UserService.verifyUser(
        noVerifyUserData.id,
        otpCode
      );

      if (verifyAccountResponse.success) {
        const data = verifyAccountResponse.data as {
          executedAction: boolean;
          user: User;
        };
        messageApi.success(
          t('scenes.login.ohla_login.scene.messages.account_activated_success'),
          3.5
        );
        setIsOtpModalOpen(false);
        const currentUser = data.user as User;
        dispatch(ImpersonationAction.clearImpersonation());
        dispatch(AuthAction.signIn(currentUser));
        const dashboardConfig = getFormattedDashboardConfig(currentUser);
        dispatch(DashboardConfigAction.save(dashboardConfig));
      } else {
        setOtpInputHaveError(true);
        const error = verifyAccountResponse.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t('scenes.login.ohla_login.scene.errors.activate_account_failed'),
          3.5
        );
        setOtpLoading(false);
      }
    }
  };

  const handleOtpCancel = () => {
    setIsOtpModalOpen(false);
  };

  const onChange: GetProp<typeof Input.OTP, 'onChange'> = (text) => {
    console.log('onChange:', text);
  };

  const sharedProps: OTPProps = {
    onChange,
  };

  const resendVerifyCode = async () => {
    if (noVerifyUserData?.id) {
      setOtpLoading(true);

      messageApi.open({
        type: 'loading',
        content: t(
          'scenes.login.ohla_login.scene.messages.sending_activation_code'
        ),
        duration: 0,
      });

      const sendActivationCodeResponse =
        await UserService.sendNewActivationCode(noVerifyUserData.id);

      messageApi.destroy();

      if (sendActivationCodeResponse.success) {
        const sendActivationCodeResponseData =
          sendActivationCodeResponse.data as {
            executedAction: boolean;
            userId: number;
            expiredCodeAt: Date;
          };
        messageApi.success(
          t(
            'scenes.login.ohla_login.scene.messages.send_activation_code_success'
          ),
          3.5
        );
        setOtpCode('');
        setExpiredOtp(
          dayjs(sendActivationCodeResponseData.expiredCodeAt)
            .subtract(20, 'second')
            .toDate()
        );
      } else {
        const error = sendActivationCodeResponse.data as ApiErrorData;
        messageApi.error(
          (error.message as string) ||
            t(
              'scenes.login.ohla_login.scene.errors.send_activation_code_failed'
            ),
          3.5
        );
      }
      setOtpLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <ConfigProvider
        locale={i18n.language === 'en' ? enUS : esES}
        theme={{
          token: {
            colorPrimary: OHLA_DEFAULT_PRIMARY_COLOR,
          },
        }}
      >
        <Spin spinning={loading}>
          <div className="login-page">
            <div className="login-box">
              <div className="login-logo-container">
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <img
                    className="login-ohla-logo-img"
                    src={LOGO_OHLA}
                    alt="logo"
                  />
                </div>
                <div>
                  <Typography.Title
                    level={3}
                    style={{ textAlign: 'center', color: '#707070' }}
                  >
                    {t('scenes.login.ohla_login.scene.title')}
                  </Typography.Title>
                  <Paragraph style={{ textAlign: 'center', color: '#707070' }}>
                    {t('scenes.login.ohla_login.scene.subtitle')}
                  </Paragraph>
                </div>
              </div>

              <Form
                name="login-form"
                initialValues={{ remember: true }}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
              >
                <Form.Item
                  name="username"
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.login.ohla_login.scene.validations.username.required'
                      ),
                    },
                  ]}
                >
                  <Input
                    placeholder={t(
                      'scenes.login.ohla_login.scene.placeholders.username'
                    )}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t(
                        'scenes.login.ohla_login.scene.validations.password.required'
                      ),
                    },
                    {
                      min: 4,
                      message: t(
                        'scenes.login.ohla_login.scene.validations.password.min_length'
                      ),
                    },
                  ]}
                >
                  <Password
                    placeholder={t(
                      'scenes.login.ohla_login.scene.placeholders.password'
                    )}
                  />
                </Form.Item>

                <Form.Item name="remember" valuePropName="checked">
                  <Checkbox>
                    {t('scenes.login.ohla_login.scene.checkbox.remember_me')}
                  </Checkbox>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="login-form-button"
                    loading={loading}
                  >
                    {t('scenes.login.ohla_login.scene.buttons.sign_in')}
                  </Button>
                </Form.Item>

                <p
                  className="login-form-forgotten-password"
                  onClick={() => {
                    showPasswordRecoveryModal();
                  }}
                  style={{
                    color: OHLA_DEFAULT_PRIMARY_COLOR,
                  }}
                >
                  {t('scenes.login.ohla_login.scene.buttons.forgot_password')}
                </p>
              </Form>
            </div>

            {/* Password recovery modal */}
            <Modal
              title={t(
                'scenes.login.ohla_login.components.password_recovery_modal.title'
              )}
              centered
              open={isPasswordRecoveryModalOpen}
              onOk={handlePasswordRecoveryOk}
              onCancel={handlePasswordRecoveryCancel}
              footer={[
                <Button
                  key="submit"
                  type="primary"
                  loading={passwordRecoveryLoading}
                  onClick={handlePasswordRecoveryOk}
                  icon={<MailOutlined />}
                >
                  {t(
                    'scenes.login.ohla_login.components.password_recovery_modal.buttons.send'
                  )}
                </Button>,
              ]}
            >
              <p>
                {t(
                  'scenes.login.ohla_login.components.password_recovery_modal.messages.content'
                )}
              </p>
              <Input
                type="text"
                style={{ marginTop: '15px' }}
                status={emailInputHaveError ? 'error' : undefined}
                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder={t(
                  'scenes.login.ohla_login.components.password_recovery_modal.placeholders.user_account'
                )}
                value={emailInputRecoveryPassword}
                onChange={(newValue) => {
                  setEmailInputRecoveryPassword(newValue.target.value);
                }}
                onFocus={() => {
                  setEmailInputHaveError(false);
                }}
              />
            </Modal>

            {/* Verify user modal */}
            <Modal
              title={t(
                'scenes.login.ohla_login.components.verify_user_modal.title'
              )}
              centered
              open={isVerifyUserModalOpen}
              onOk={handleVerifyUserOk}
              onCancel={handleVerifyUserCancel}
              footer={[
                <Button
                  key="submit"
                  type="primary"
                  loading={passwordRecoveryLoading}
                  onClick={handleVerifyUserOk}
                  icon={<MailOutlined />}
                >
                  {t(
                    'scenes.login.ohla_login.components.verify_user_modal.buttons.send'
                  )}
                </Button>,
              ]}
            >
              <p>
                {t(
                  'scenes.login.ohla_login.components.verify_user_modal.messages.content'
                )}
              </p>
            </Modal>

            {/* OTP modal */}
            <Modal
              title={t('scenes.login.ohla_login.components.otp_modal.title')}
              centered
              open={isOtpModalOpen}
              onOk={handleOtpOk}
              onCancel={handleOtpCancel}
              footer={null}
            >
              <p style={{ marginTop: '10px' }}>
                {t(
                  'scenes.login.ohla_login.components.otp_modal.messages.content'
                )}
              </p>
              <Flex
                gap="large"
                align="center"
                vertical
                style={{ marginTop: '30px' }}
              >
                <Input.OTP
                  autoFocus
                  status={otpInputHaveError ? 'error' : undefined}
                  disabled={progressOtp === 0 || otpLoading}
                  formatter={(str) => str.toUpperCase()}
                  {...sharedProps}
                  length={4}
                  value={progressOtp <= 0 ? '' : otpCode}
                  onChange={(newValue) => {
                    setOtpCode(newValue);
                  }}
                  onFocus={() => {
                    setOtpInputHaveError(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleOtpOk();
                    }
                  }}
                />

                <Button
                  key="submit"
                  type="primary"
                  loading={otpLoading}
                  onClick={handleOtpOk}
                  disabled={otpCode.length !== 4}
                >
                  {t(
                    'scenes.login.ohla_login.components.otp_modal.buttons.verify_user'
                  )}
                </Button>

                <Popconfirm
                  title={t(
                    'scenes.login.ohla_login.components.otp_modal.popconfirm.title'
                  )}
                  description={t(
                    'scenes.login.ohla_login.components.otp_modal.popconfirm.content'
                  )}
                  okText={t(
                    'scenes.login.ohla_login.components.otp_modal.popconfirm.ok_text'
                  )}
                  cancelText={t(
                    'scenes.login.ohla_login.components.otp_modal.popconfirm.cancel_text'
                  )}
                  onConfirm={() => {
                    resendVerifyCode();
                  }}
                >
                  <p
                    className="login-form-forgotten-password"
                    style={{
                      color: OHLA_DEFAULT_PRIMARY_COLOR,
                    }}
                  >
                    {t(
                      'scenes.login.ohla_login.components.otp_modal.resend_link_text'
                    )}
                  </p>
                </Popconfirm>
              </Flex>
            </Modal>
          </div>
        </Spin>
      </ConfigProvider>
    </>
  );
}

export default OhlaLogin;
